/**
 * The pixel meadow of the unreachable banks and gardens (lifted from
 * lantern-water's `backdrop-ground.js`): shader arithmetic on the terrain's
 * material (no pass, no texture of its own): world-space, pixel-quantised
 * value noise gives clustered tones of one hue, dry patches, worn footpaths,
 * wildflower drifts and an earthy pebbled bank at the water, and the land
 * beyond the last rank sinks into warm violet. One material per kit
 * (`meadowMaterial`), shared by the river banks and every backdrop quarter,
 * so they merge into one draw. The river coast is the closed form of
 * `river-course.ts`.
 */
import * as THREE from 'three';
import { addShaderHook } from '../material-hooks.ts';
import type { Kit } from './kit.ts';

const header = `varying vec3 bdWorld;
float bdHash(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}
float bdNoise(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
  return mix(mix(bdHash(i),bdHash(i+vec2(1,0)),f.x),mix(bdHash(i+vec2(0,1)),bdHash(i+vec2(1,1)),f.x),f.y);}
float bdSmooth(float v,float a,float b){float t=clamp((v-a)/(b-a),0.,1.);return t*t*(3.-2.*t);}
float bdCoast(float z,float east){
  float south=bdSmooth(z,16.,48.),north=bdSmooth(-z,34.,78.);
  float bend=sin(z*.12)*south*2.+sin((-z-34.)*.055)*north*3.;
  return east>.5?10.5+south*29.+north*17.+bend:3.8-south*31.-north*20.+bend;
}
float bdSeg(vec2 p,vec2 a,vec2 b){vec2 pa=p-a,ba=b-a;float h=clamp(dot(pa,ba)/dot(ba,ba),0.,1.);return length(pa-ba*h);}`;

const meadow = `
{
  vec2 q=floor(bdWorld.xz*16.)/16.;
  float h=bdHash(q*3.1);
  float t=bdNoise(q*.33)*.62+bdNoise(q*1.25+7.3)*.38+(h-.5)*.2;
  vec3 tone=t<.36?vec3(.07,.085,.035):t<.5?vec3(.11,.13,.05):t<.64?vec3(.16,.17,.07):vec3(.23,.21,.09);
  float tuft=step(.93,bdHash(floor(bdWorld.xz*vec2(16.,8.))*1.7));
  tone*=1.-tuft*.22;
  float drift=step(.62,bdNoise(q*.45+19.));
  vec2 q2=floor(bdWorld.xz*8.);
  float fl=step(.955,bdHash(q2*.53+1.1))*drift*step(.5,t);
  float kind=bdHash(q2*.97);
  vec3 flower=kind<.4?vec3(.72,.7,.6):kind<.7?vec3(.78,.56,.14):kind<.88?vec3(.66,.26,.34):vec3(.38,.3,.62);
  tone=mix(tone,flower,fl);
  vec2 p=bdWorld.xz;
  float d=min(min(bdSeg(p,vec2(-34.,19.1),vec2(1.8,18.9)),bdSeg(p,vec2(12.5,19.0),vec2(34.,19.4))),
              min(bdSeg(p,vec2(-14.,19.),vec2(-19.,30.)),bdSeg(p,vec2(24.,19.3),vec2(29.,32.))));
  vec2 qf=floor(bdWorld.xz*32.)/32.;
  float edge=.55+(bdHash(qf*2.3)-.5)*.3;
  float path=1.-step(edge,d);
  float dn=bdNoise(qf*5.)*.7+bdHash(qf*1.9)*.3;
  vec3 dirt=dn<.38?vec3(.2,.14,.085):dn<.62?vec3(.27,.19,.115):vec3(.34,.25,.15);
  dirt*=1.-step(.93,bdHash(qf*7.7))*.3;
  tone=mix(tone,dirt,path*.92);
  float coast=min(abs(p.x-bdCoast(p.y,0.)),abs(p.x-bdCoast(p.y,1.)));
  float bank=1.-bdSmooth(coast+(h-.5)*.5,1.2,2.6);
  vec3 earth=mix(vec3(.2,.15,.1),vec3(.15,.13,.11),step(.5,bdNoise(q*1.7)));
  float pebble=step(.86,bdHash(floor(p*8.)*2.9))*bank;
  earth=mix(earth,vec3(.42,.4,.35),pebble);
  tone=mix(tone,earth,bank);
  vec3 grain=diffuseColor.rgb/max(vec3(.28),vec3(.45,.48,.38));
  diffuseColor.rgb=tone*mix(vec3(1.),grain,.35);
  vec2 out2=max(max(vec2(-20.15,-16.15)-p,p-vec2(21.15,16.15)),vec2(0.));
  float beyond=length(out2);
  diffuseColor.rgb=mix(diffuseColor.rgb,vec3(.03,.024,.034),smoothstep(13.,30.,beyond)*.9);
}`;

/** The kit's meadow material (`world.js` `townSurroundings`'s terrain material): the grass map, vertex colours, the meadow shader. */
export const meadowMaterial = (kit: Kit): THREE.MeshStandardMaterial =>
  kit.shared('meadow', () => {
    const material = new THREE.MeshStandardMaterial({ map: kit.maps.grass, vertexColors: true, roughness: 1 });
    addShaderHook(material, {
      key: 'town-meadow',
      vertexHeader: 'varying vec3 bdWorld;',
      fragmentHeader: header,
      vertex: [{ chunk: 'worldpos_vertex', after: 'bdWorld=(modelMatrix*vec4(transformed,1.)).xyz;' }],
      fragment: [{ chunk: 'color_fragment', after: meadow }],
    });
    return material;
  });

/** A flat meadow quad sharing the terrain's material (and so its draw): position, normal, uv and colour, as the terrain has. */
export const meadowPatch = (parent: THREE.Object3D, material: THREE.Material, x0: number, x1: number, z0: number, z1: number, y: number): THREE.Mesh => {
  const g = new THREE.PlaneGeometry(x1 - x0, z1 - z0);
  g.rotateX(-Math.PI / 2);
  g.translate((x0 + x1) / 2, y, (z0 + z1) / 2);
  const n = g.getAttribute('position').count;
  const tint = [0.55, 0.56, 0.44];
  g.setAttribute('color', new THREE.Float32BufferAttribute(Array.from({ length: n * 3 }, (_, i) => tint[i % 3] ?? 0), 3));
  const m = new THREE.Mesh(g, material);
  m.receiveShadow = true;
  parent.add(m);
  return m;
};
